import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { IonSelect } from '@ionic/angular/standalone';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

/**
 * Ionic's SelectValueAccessor on inner `ion-select` resolves ancestor `NgControl` (the host's
 * ngModel) and copies `ng-invalid` onto the inner select — a false "error" outline while the
 * real control is valid. `viewProviders` shadows `NgControl` for this template only so the inner
 * accessor no-ops; the host still uses {@link NG_VALUE_ACCESSOR} for ngModel / formControlName.
 */
@Component({
  selector: 'crowdctrl-select',
  standalone: true,
  imports: [IonSelect],
  templateUrl: './select.component.html',
  styleUrls: ['../form-outline-field-tokens.css', './select.component.css'],
  viewProviders: [{ provide: NgControl, useValue: null }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  @Input() placeholder?: string;
  @Input() label?: string;
  /** Ionic fill style. Use `null` to disable fill (no outline/solid). */
  @Input() fill: 'outline' | 'solid' | null = 'outline';
  /** Ionic: `start` | `end` | `floating` | `stacked` | `fixed`. Use `start` to avoid MD floating-outline look. */
  @Input() labelPlacement: string = 'stacked';
  /** Ionic ion-select: `start` | `end` | `space-between`. */
  @Input() justify: string = 'space-between';
  /** Ionic: `ios` | `md` — optional; `ios` often matches classic non-floating label behavior. */
  @Input() mode?: string;
  @Input() helperText?: string;
  @Input() required = false;
  @Input() name?: string;
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() interfaceOptions: Record<string, any> = {};
  @Input() compareWith?: (o1: any, o2: any) => boolean;
  @Input() cancelText?: string;
  @Input() okText?: string;
  @Input() selectedText?: string;

  @Output() ionChange = new EventEmitter<CustomEvent<{ value?: any }>>();
  @Output() ionBlur = new EventEmitter<CustomEvent<any>>();
  @Output() ionFocus = new EventEmitter<CustomEvent<any>>();

  @Input()
  set value(value: any) {
    this.writeValue(value);
  }

  currentValue: any = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.currentValue = value ?? null;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleChange(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: any }>;
    const detailValue = customEvent.detail as { value?: any } | undefined;
    const value = detailValue?.value ?? null;
    this.currentValue = value;
    this.onChange(value);
    this.ionChange.emit(customEvent);
  }

  handleBlur(event: Event): void {
    const customEvent = event as CustomEvent<any>;
    this.onTouched();
    this.ionBlur.emit(customEvent);
  }
}
